// Unified diff, the way git shows one — pure functions, no React.
//
// Lives outside the Docs page so the logic can be unit-tested directly
// (node frontend/src/components/docs/diff.test.mjs). Showing a whole
// document and calling it a diff makes the reader hunt for the change; a
// hunk with context puts it in front of them.

export const DIFF_CONTEXT = 3;
export const DIFF_MAX_LINES = 4000;

// An empty document has ZERO lines, not one empty line — `"".split("\n")`
// returns [""], which would render a phantom deleted line on the very first
// revision (whose base is empty by definition).
function splitLines(text) {
  const s = String(text ?? "");
  return s === "" ? [] : s.split("\n").slice(0, DIFF_MAX_LINES);
}

// True when either side is too large to diff at O(n*m). The caller shows the
// documents instead of silently diffing only the first N lines — a diff that
// quietly stops halfway is worse than no diff.
export function tooLargeToDiff(oldText, newText) {
  return splitLines(oldText).length >= DIFF_MAX_LINES || splitLines(newText).length >= DIFF_MAX_LINES;
}

export function lineDiffRows(oldText, newText) {
  const a = splitLines(oldText);
  const b = splitLines(newText);
  const n = a.length, m = b.length;
  // LCS length table, walked backwards. Uint32 because a long doc pair can
  // exceed the 65535 a Uint16 holds.
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const rows = [];
  let i = 0, j = 0;
  const push = (kind, text) => rows.push({
    kind, text,
    oldNo: kind === "add" ? null : i,      // 1-based line numbers, assigned
    newNo: kind === "del" ? null : j,      // from the cursors before advancing
  });
  while (i < n && j < m) {
    if (a[i] === b[j]) { push("same", a[i]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { push("del", a[i]); i++; }
    else { push("add", b[j]); j++; }
  }
  while (i < n) { push("del", a[i]); i++; }
  while (j < m) { push("add", b[j]); j++; }
  // The push() closure captured the pre-increment cursors; make them 1-based.
  return rows.map((r) => ({
    ...r,
    oldNo: r.oldNo == null ? null : r.oldNo + 1,
    newNo: r.newNo == null ? null : r.newNo + 1,
  }));
}

export function toHunks(rows, context = DIFF_CONTEXT) {
  const changed = rows.map((r) => r.kind !== "same");
  if (!changed.some(Boolean)) return [];
  // Keep any line within `context` of a change.
  const keep = rows.map((_, idx) =>
    changed.slice(Math.max(0, idx - context), idx + context + 1).some(Boolean));
  const hunks = [];
  let cur = null;
  rows.forEach((r, idx) => {
    if (!keep[idx]) { cur = null; return; }
    if (!cur) {
      cur = { oldStart: r.oldNo ?? 0, newStart: r.newNo ?? 0, oldLines: 0, newLines: 0, rows: [] };
      hunks.push(cur);
    }
    if (r.kind !== "add") cur.oldLines++;
    if (r.kind !== "del") cur.newLines++;
    cur.rows.push(r);
  });
  return hunks;
}

export function diffStats(rows) {
  let add = 0, del = 0;
  for (const r of rows) { if (r.kind === "add") add++; else if (r.kind === "del") del++; }
  return { add, del };
}

